import {Select} from '@mantine/core';
import {Anchor} from 'src/design/atoms/Anchor';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import type {ReactNode} from 'react';
import React, {useState} from 'react';
import type {UserInstanceModel} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';

export interface InstancePickerProps {
  instances: UserInstanceModel[];
  currentInstanceId?: number;
  onSelect: (instanceId: number) => void;
  onBack?: () => void;
  isLoading?: boolean;
  description?: ReactNode;
  submitLabel?: string;
  backLabel?: string;
}

export const InstancePicker: React.FC<InstancePickerProps> = ({
  instances,
  currentInstanceId,
  onSelect,
  onBack,
  isLoading,
  description = 'Your account has access to multiple companies. Select the company you want to log in to.',
  submitLabel = 'Continue',
  backLabel = 'Use a different email',
}) => {
  const defaultId =
    instances.find(i => i.id === currentInstanceId)?.id ?? instances[0]?.id;
  const [selectedId, setSelectedId] = useState<string | null>(
    defaultId !== undefined ? String(defaultId) : null
  );

  const options = instances.map(i => ({
    value: String(i.id),
    label: i.name ?? '',
  }));

  const handleContinue = () => {
    if (!selectedId) return;
    onSelect(Number(selectedId));
  };

  return (
    <Stack>
      <Text size="sm" c="dimmed" ta="center">
        {description}
      </Text>

      <Select
        label="Company"
        data={options}
        value={selectedId}
        onChange={setSelectedId}
        placeholder="Select a company..."
        searchable
        allowDeselect={false}
      />

      <Button
        fullWidth
        loading={isLoading}
        disabled={!selectedId}
        onClick={handleContinue}>
        {submitLabel}
      </Button>

      {onBack && (
        <Anchor
          size="sm"
          c="dimmed"
          ta="center"
          component="button"
          onClick={onBack}>
          {backLabel}
        </Anchor>
      )}
    </Stack>
  );
};
